import { Ban } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import type { ComponentProps, PropsWithChildren, ReactNode } from 'react'
import type { ConnectingWallet } from '@/src/hooks/wallet/use-wallet-options'
import { cn } from '@/src/utils/styles'

interface WalletStateProps extends ComponentProps<'div'> {
  walletLogo?: string
  walletName?: string
  title?: string
  titleClassName?: string
  desc?: string
  totalEcosystemCount?: number
  connectedEcosystemCount?: number
  actions?: ReactNode
  state: 'error' | 'connecting'
  errorType?: NonNullable<ConnectingWallet['error']>['type'] | undefined
  mode?: 'spin' | 'card'
  animationKey?: string
  descContainerClassName?: string
}

export const WalletState = ({
  walletLogo,
  title,
  titleClassName,
  desc,
  totalEcosystemCount,
  connectedEcosystemCount,
  actions,
  walletName,
  ref,
  state,
  className,
  errorType,
  mode = 'card',
  animationKey,
  descContainerClassName,
}: PropsWithChildren<WalletStateProps>) => {
  if (mode === 'spin') {
    return (
      <SpinUI
        ref={ref}
        className={className}
        walletLogo={walletLogo}
        walletName={walletName}
        animationKey={animationKey}
        title={title}
        desc={desc}
        actions={actions}
        errorType={errorType}
        state={state}
        descContainerClassName={descContainerClassName}
      />
    )
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      ref={ref}
      className={cn(
        'flex flex-col items-center justify-center p-8 mb-6',
        className,
      )}
    >
      <div
        className={cn('flex flex-col gap-8 text-center', {
          'pb-8': state === 'connecting',
        })}
      >
        <div className='flex flex-col gap-3'>
          {title && (
            <h1
              className={cn(
                'text-4xl font-serif font-extralight text-primary',
                titleClassName,
              )}
            >
              {title}
            </h1>
          )}
          {desc && <p className='text-secondary'>{desc}</p>}
        </div>
        <div className='flex w-full justify-center'>
          {walletName && walletLogo && (
            <WalletCard
              walletName={walletName}
              walletLogo={walletLogo}
              totalSteps={totalEcosystemCount}
              currentStep={connectedEcosystemCount}
              isRejected={state === 'error' && errorType === 'rejected'}
            />
          )}
        </div>
        <span className='text-sm text-tertiary'>
          This action doesn’t import your account or grant access to its funds
          without the wallet connected.
        </span>
      </div>
      {actions}
    </motion.div>
  )
}

WalletState.displayName = 'WalletState'

function WalletCard({
  walletName,
  walletLogo,
  totalSteps,
  currentStep,
  isRejected,
}: {
  walletName: string
  walletLogo: string
  totalSteps?: number
  currentStep?: number
  isRejected?: boolean
}) {
  return (
    <div
      className='z-10 w-[248px] h-[136px] rounded-2xl overflow-hidden bg-background bg-gradient-to-br from-10% from-surface3-100 via-50% via-foreground to-70% to-surface2-100 flex transition-shadow text-left shadow-lg hover:shadow-xl'
      style={
        {
          '--wallet-bg': 'rgba(58, 66, 58, 0.6)',
        } as React.CSSProperties
      }
    >
      <div
        className='p-3 pb-2 flex flex-col gap-2 w-full justify-between rounded-2xl'
        style={{
          backgroundColor: 'var(--wallet-bg)',
        }}
      >
        <div className='flex items-start justify-between w-full'>
          {walletLogo && (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              className='size-6 overflow-hidden rounded-md'
              src={walletLogo}
              alt={`${walletName} logo`}
              width={24}
              height={24}
            />
          )}
          {!isRejected ? (
            currentStep !== undefined &&
            totalSteps !== undefined &&
            totalSteps > 0 && (
              <WalletCardProgress
                step={currentStep > 0 ? currentStep - 1 : 0}
                totalSteps={totalSteps}
              />
            )
          ) : (
            <Ban size={24} className='text-danger-text' />
          )}
        </div>
        <div className='flex flex-col tracking-tight text-left'>
          <div className='text-primary font-semibold text-ellipsis overflow-hidden whitespace-nowrap'>
            {walletName}
          </div>
          {currentStep !== undefined &&
            totalSteps !== undefined &&
            totalSteps > 0 && (
              <span className='text-secondary'>
                {currentStep}/{totalSteps} approvals pending
              </span>
            )}
        </div>
      </div>
    </div>
  )
}

function WalletCardProgress({
  step,
  totalSteps,
}: {
  step: number
  totalSteps: number
}) {
  const circumference = Math.PI * 2 * 10 // 2πr
  const progress = step / totalSteps
  const strokeDashoffset = circumference - progress * circumference

  return (
    <svg className='size-6' viewBox='0 0 24 24'>
      <circle
        className='text-border1'
        fill='transparent'
        strokeWidth={2}
        stroke='currentColor'
        r={10}
        cx='12'
        cy='12'
      />
      <circle
        className='text-accent-primary -rotate-90 origin-[50%_50%] transform transition-all duration-300'
        fill='transparent'
        strokeWidth={2}
        stroke='currentColor'
        strokeDasharray={circumference}
        strokeDashoffset={strokeDashoffset}
        r={10}
        cx='12'
        cy='12'
      />
    </svg>
  )
}

function SpinUI({
  walletLogo,
  animationKey,
  ref,
  className,
  title,
  desc,
  actions,
  titleClassName,
  state,
  descContainerClassName,
}: WalletStateProps) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      ref={ref}
      className={cn(
        'relative flex flex-col items-center justify-center px-5 py-[84px]',
        className,
      )}
    >
      <div className='relative flex w-full items-center justify-center py-6 h-[176px]'>
        {state === 'connecting' ? (
          <div className='absolute size-32 z-10 rounded-full border-t-accent-primary border-b-accent-primary border-4 border-transparent animate-spin-slow' />
        ) : (
          <Ban
            size={128}
            strokeWidth={0.5}
            className='text-danger-text absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10'
          />
        )}
        {walletLogo && typeof walletLogo === 'string' && (
          <WalletLogo
            walletLogo={walletLogo}
            animationKey={animationKey}
            className={cn({
              'opacity-50': state === 'error',
            })}
          />
        )}
      </div>
      {title && (
        <h6
          className={cn(
            'text-4xl font-pp-editorial-new font-extralight text-primary',
            titleClassName,
          )}
        >
          {title}
        </h6>
      )}
      <div
        className={cn(
          'flex flex-col items-center justify-center',
          descContainerClassName,
        )}
      >
        <span className='text-secondary text-center py-3'>{desc}</span>
        {actions}
      </div>
    </motion.div>
  )
}

function WalletLogo({
  walletLogo,
  animationKey,
  className,
}: {
  walletLogo: string
  animationKey?: string
  className?: string
}) {
  if (animationKey) {
    return (
      <AnimatePresence mode='wait'>
        <motion.div
          layoutId={animationKey}
          layout
          transition={{
            duration: 0.4,
            type: 'spring',
            stiffness: 300,
            damping: 30,
          }}
        >
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            width={72}
            height={72}
            src={walletLogo}
            alt='wallet logo'
            className={className}
          />
        </motion.div>
      </AnimatePresence>
    )
  }

  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      width={72}
      height={72}
      src={walletLogo}
      alt='wallet logo'
      className={className}
    />
  )
}
